import { useState } from 'react';
import { Link, useLocation } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import { Icon } from '@/components/ui/icon';
import { Media } from '@/components/ui/media';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { useStrings } from '@/i18n/use-strings';
import { themeForAudience, useUserStore } from '@/store/user';
import { themeFromPathname } from '@/utils/theme';
import { LanguageSheet } from './language-sheet';
import { MenuSheet } from './menu-sheet';

/**
 * How much chrome a screen wants:
 *
 * - `logo` — identity only. Onboarding steps that must not offer an exit.
 * - `logo-language` — identity plus the language pill. The name screen.
 * - `full` — identity, the `progress` slot, the language pill and the menu.
 *   Every screen from the journey onward.
 */
export type AppHeaderVariant = 'logo' | 'logo-language' | 'full';

/**
 * The persistent header: white bar, navy text, blue chrome actions, sticky at
 * the top of the viewport.
 *
 * `progress` is a slot rather than a prop shape, because the journey and the
 * chat show different indicators (`OverallProgress` here, the module line and
 * checkpoints in chat) and only ever one at a time. It renders on its own row
 * under the identity row: at 390 px there is no honest way to fit four labelled
 * segments beside a logo and two 44 px controls, and a full-width row also
 * gives chat's checkpoint strip the space it needs.
 *
 *   <AppHeader variant="full" progress={<OverallProgress done={2} labels={titles} />} />
 */
export function AppHeader({
  variant = 'full',
  progress,
  backHref,
  wide = false,
  className,
}: {
  variant?: AppHeaderVariant;
  /** Rendered under the identity row when `variant` is `full`. */
  progress?: React.ReactNode;
  /**
   * Adds a leading back arrow. Only a module needs one — every other screen is
   * a destination, and the logo already goes to the journey.
   */
  backHref?: string;
  /**
   * The journey widens to two columns from `lg` up. Set this there so the bar's
   * contents share that measure — the logo and the progress rule otherwise
   * ended mid-air, 220 px inside the column of cards below them.
   */
  wide?: boolean;
  className?: string;
}) {
  const { header, content, chat } = useStrings();
  const { siteSettings, languages, language } = useRootLoaderData();
  const headerLogoHeight = siteSettings.logoHeights.header;

  const profile = useUserStore((state) => state.profile);
  const { pathname } = useLocation();

  const [languageOpen, setLanguageOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // The URL is the truth on a themed screen; content pages have no theme
  // segment, so the profile decides where the menu's Journey link points.
  const theme = themeFromPathname(pathname) ?? (profile ? themeForAudience(profile.audience) : 'ocean');

  const home = profile?.name ? `/h/${theme}/learn/topics` : '/';
  const active = languages.find((option) => option.code === language);

  // The kids course is English only, so there is nothing to switch to.
  const showLanguage = variant !== 'logo' && profile?.audience !== 'child';
  const showMenu = variant === 'full';
  const showProgress = variant === 'full' && progress !== undefined && progress !== null;

  return (
    <header
      // `safe-top` puts back the inset that `viewport-fit=cover` gave away, so
      // the bar clears the notch instead of sliding under it.
      className={cn('border-surf-gray safe-top sticky top-0 z-40 border-b bg-white', className)}
      data-slot="app-header"
    >
      {/* First focusable element on the page, so keyboard users can jump past
          the chrome that repeats on every screen. */}
      <a
        href="#main-content"
        className="focus-visible:bg-surf-blue sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:start-3 focus-visible:top-3 focus-visible:z-50 focus-visible:rounded-full focus-visible:px-4 focus-visible:py-2 focus-visible:text-sm focus-visible:font-semibold focus-visible:text-white"
      >
        {content.content_skip_to_content}
      </a>

      {/*
        `max-w-2xl` is the app's one measure — the chat column, the passport
        card and the content pages all use it, so the logo sits over the left
        edge of the page's content instead of 50 px outside it on a desktop
        screen. The journey is the one screen that widens past it, and says so
        through `wide`.
      */}
      <div
        className={cn(
          'mx-auto flex min-h-14 w-full max-w-2xl items-center gap-1.5 px-4 sm:gap-2 sm:px-6',
          wide && 'lg:max-w-5xl',
        )}
      >
        {backHref ? (
          <Link
            to={backHref}
            aria-label={chat.chat_back}
            className={cn(
              'text-surf-blue -ms-2 grid size-11 shrink-0 place-items-center rounded-full',
              'transition-[background-color,scale] duration-150 active:scale-96 motion-reduce:transition-none',
              'hover:bg-surf-blue/[0.08] focus-visible:ring-surf-blue/40 focus-visible:ring-4 focus-visible:outline-none',
            )}
          >
            <Icon name="passport/chevron-left" aria-hidden className="size-5 rtl:rotate-180" />
          </Link>
        ) : null}

        <Link
          to={home}
          aria-label={siteSettings.seo.defaultTitle}
          className="focus-visible:ring-surf-blue/40 flex min-h-11 shrink-0 items-center rounded-xl focus-visible:ring-4 focus-visible:outline-none"
        >
          {/*
            Straight onto the white bar: `header_logo` is the full-colour
            lockup (navy wordmark, positive Qantas mark), so it needs no plate
            behind it. The white `profile_logo` artwork is for the photograph
            on the onboarding screens, not here.

            The logo's height is fixed, so the bar cannot resize while it
            decodes; `w-auto` keeps the asset's own aspect. It is drawn as tall
            as the bar allows so the lockup's small partner line stays legible.
            `header_logo_height` replaces both breakpoints with one size.
          */}
          <Media
            src={siteSettings.headerLogoUrl}
            alt=""
            width={818}
            height={204}
            priority
            placeholder="transparent"
            className={cn('w-auto', headerLogoHeight === null && 'h-10 sm:h-11')}
            style={headerLogoHeight === null ? undefined : { height: headerLogoHeight }}
          />
        </Link>

        <span className="flex-1" />

        {showLanguage ? (
          <button
            type="button"
            onClick={() => setLanguageOpen(true)}
            aria-label={`${header.header_language_label}: ${active?.nativeName ?? language}`}
            className={cn(
              'text-surf-blue flex min-h-11 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-bold',
              'transition-[background-color,scale] duration-150 active:scale-96 motion-reduce:transition-none',
              'hover:bg-surf-blue/[0.08] focus-visible:ring-surf-blue/40 focus-visible:ring-4 focus-visible:outline-none',
            )}
          >
            <Icon name="passport/globe" aria-hidden className="size-5" />
            <span aria-hidden className="tracking-wide uppercase">
              {language}
            </span>
          </button>
        ) : null}

        {showMenu ? (
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={header.header_menu_label}
            className={cn(
              'text-surf-blue -me-1 grid size-11 shrink-0 place-items-center rounded-full',
              'transition-[background-color,scale] duration-150 active:scale-96 motion-reduce:transition-none',
              'hover:bg-surf-blue/[0.08] focus-visible:ring-surf-blue/40 focus-visible:ring-4 focus-visible:outline-none',
            )}
          >
            <Icon name="passport/menu" aria-hidden className="size-6" />
          </button>
        ) : null}
      </div>

      {showProgress ? (
        // The rule spans the full bar, not just the content column.
        <div className="border-surf-gray border-t">
          <div className={cn('mx-auto w-full max-w-2xl px-4 py-2 sm:px-6', wide && 'lg:max-w-5xl')}>{progress}</div>
        </div>
      ) : null}

      {showLanguage ? <LanguageSheet open={languageOpen} onOpenChange={setLanguageOpen} /> : null}
      {showMenu ? <MenuSheet open={menuOpen} onOpenChange={setMenuOpen} theme={theme} /> : null}
    </header>
  );
}
